import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Button, Stack, IconButton, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    Sun, Moon, TrendingUp, BarChart2, Zap,
    ShieldCheck, ArrowRight, Activity,
} from "lucide-react";
import { useTheme as useAppTheme } from '../../context/ThemeContext'; // adjust path if needed

/* ══════════════════════════════════════════════
   CANDLESTICK CANVAS BACKGROUND
══════════════════════════════════════════════ */
const CandlestickChartBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf, candles = [], ma = [], vols = [], scrollX = 0;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        const gen = (prev) => {
            const o = prev + (Math.random() - 0.5) * 12;
            const c = o + (Math.random() - 0.5) * 18;
            return {
                o, c,
                h: Math.max(o, c) + Math.random() * 8,
                l: Math.min(o, c) - Math.random() * 8,
                v: Math.random() * 50 + 20,
            };
        };

        let last = 280;
        const n = Math.ceil(window.innerWidth / 24) + 10;
        for (let i = 0; i < n; i++) {
            const cd = gen(last);
            candles.push(cd);
            vols.push(cd.v);
            last = cd.c;
            ma.push(
                i >= 19
                    ? candles.slice(i - 19, i + 1).reduce((a, c) => a + c.c, 0) / 20
                    : null
            );
        }

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const W = 18, S = 24, CH = canvas.height - 100;
            const prices = candles.flatMap((c) => [c.h, c.l]);
            const mn = Math.min(...prices) - 15;
            const mx = Math.max(...prices) + 15;
            const rng = mx - mn;
            const sy = (p) => 30 + ((mx - p) / rng) * (CH - 60);
            const mvol = Math.max(...vols);

            ctx.strokeStyle = "rgba(100,116,139,.08)";
            ctx.lineWidth = 1;
            for (let i = 0; i < 6; i++) {
                const y = 30 + (CH - 60) * (i / 5);
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            ctx.strokeStyle = "rgba(234,179,8,.5)";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            let started = false;
            candles.forEach((_, i) => {
                if (ma[i] == null) return;
                const x = i * S - scrollX + W / 2;
                const y = sy(ma[i]);
                if (x < -S || x > canvas.width + S) return;
                if (started) ctx.lineTo(x, y);
                else { ctx.moveTo(x, y); started = true; }
            });
            ctx.stroke();

            candles.forEach((cd, i) => {
                const x = i * S - scrollX;
                if (x < -S || x > canvas.width + S) return;
                const bull = cd.c > cd.o;
                ctx.strokeStyle = bull ? "rgba(16,185,129,.55)" : "rgba(239,68,68,.55)";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x + W / 2, sy(cd.h));
                ctx.lineTo(x + W / 2, sy(cd.l));
                ctx.stroke();
                ctx.fillStyle = bull ? "rgba(16,185,129,.7)" : "rgba(239,68,68,.7)";
                ctx.fillRect(x, sy(Math.max(cd.o, cd.c)), W - 2, Math.max(Math.abs(sy(cd.o) - sy(cd.c)), 2));
                ctx.fillStyle = "rgba(59,130,246,.2)";
                ctx.fillRect(x, canvas.height - (cd.v / mvol) * 60 - 8, W - 2, (cd.v / mvol) * 60);
            });

            scrollX += 0.4;
            if (scrollX >= S) {
                scrollX -= S;
                candles.shift(); ma.shift(); vols.shift();
                const nc = gen(candles[candles.length - 1].c);
                candles.push(nc);
                vols.push(nc.v);
                ma.push(candles.slice(-20).reduce((a, c) => a + c.c, 0) / 20);
            }
            raf = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            window.removeEventListener("resize", resize);
            cancelAnimationFrame(raf);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.3 }}
        />
    );
};

/* ══════════════════════════════════════════════
   SIGNAL TERMINAL WIDGET
══════════════════════════════════════════════ */
const SIGNALS = [
    { sym: "NVDA", action: "BUY", price: "$892.40", score: 94, color: "success" },
    { sym: "AAPL", action: "HOLD", price: "$213.10", score: 61, color: "warning" },
    { sym: "TSLA", action: "SELL", price: "$198.75", score: 22, color: "error" },
    { sym: "META", action: "BUY", price: "$521.80", score: 88, color: "success" },
    { sym: "MSFT", action: "BUY", price: "$412.30", score: 79, color: "info" },
    { sym: "AMZN", action: "HOLD", price: "$195.60", score: 55, color: "warning" },
];

const SignalTerminal = () => {
    const theme = useTheme();
    const [visible, setVisible] = useState(1);
    const [sentiment, setSentiment] = useState(72);

    useEffect(() => {
        const t = setInterval(() => setVisible((v) => (v < SIGNALS.length ? v + 1 : v)), 900);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const t = setInterval(
            () => setSentiment((s) => Math.min(99, Math.max(30, s + (Math.random() > 0.5 ? 1 : -1)))),
            2000
        );
        return () => clearInterval(t);
    }, []);

    const resolveColor = (key) => theme.palette[key]?.main ?? theme.palette.primary.main;

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: 480,
                borderRadius: "var(--radius-xl)",
                border: "1px solid var(--color-border-primary)",
                overflow: "hidden",
                bgcolor: "background.paper",
                boxShadow: "var(--shadow-lg)",
                animation: "slideLeft 0.7s ease both",
                "@keyframes slideLeft": {
                    from: { opacity: 0, transform: "translateX(40px)" },
                    to: { opacity: 1, transform: "translateX(0)" },
                },
            }}
        >
            {/* Title bar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2, py: 1.5,
                    bgcolor: "background.secondary",
                    borderBottom: "1px solid var(--color-border-primary)",
                }}
            >
                <Stack direction="row" spacing={0.75}>
                    {["#EF4444", "#F59E0B", "#10B981"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c }} />
                    ))}
                </Stack>
                <Typography variant="tableHeader" sx={{ letterSpacing: "0.1em", textTransform: "none" }}>
                    FactorAlpha
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box
                        sx={{
                            width: 6, height: 6, borderRadius: "50%",
                            bgcolor: "success.main",
                            animation: "pulse 1.5s ease infinite",
                            "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                        }}
                    />
                    <Typography variant="tableHeader" sx={{ color: "success.main" }}>LIVE</Typography>
                </Stack>
            </Box>

            {/* Column headers */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 72px 96px 84px",
                    px: 2, py: 1,
                    borderBottom: "1px solid var(--color-border-primary)",
                }}
            >
                {["Symbol", "Action", "FA Score"].map((h, i) => (
                    <Typography
                        key={h}
                        variant="tableHeader"
                        sx={{ textAlign: i === 3 ? "right" : "left", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                        {h}
                    </Typography>
                ))}
            </Box>

            {/* Signal rows */}
            <Box sx={{ minHeight: 240 }}>
                {SIGNALS.slice(0, visible).map((s, i) => {
                    const col = resolveColor(s.color);
                    return (
                        <Box
                            key={s.sym}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 72px 96px 84px",
                                px: 2, py: 1.25,
                                borderBottom: "1px solid var(--color-border-primary)",
                                bgcolor: i % 2 !== 0 ? "surface.hover" : "transparent",
                                alignItems: "center",
                                animation: "fadeUp 0.4s ease both",
                                animationDelay: `${i * 0.08}s`,
                                "@keyframes fadeUp": {
                                    from: { opacity: 0, transform: "translateY(8px)" },
                                    to: { opacity: 1, transform: "translateY(0)" },
                                },
                            }}
                        >
                            {/* Symbol */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box
                                    sx={{
                                        width: 28, height: 28,
                                        borderRadius: "var(--radius-sm)",
                                        bgcolor: `${col}22`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >
                                    <Typography variant="chipText" sx={{ color: col, fontWeight: "var(--font-weight-bold)", fontSize: "var(--font-size-xs2)" }}>
                                        {s.sym[0]}
                                    </Typography>
                                </Box>
                                <Typography variant="chipText" sx={{ color: "text.primary", fontWeight: "var(--font-weight-semibold)" }}>
                                    {s.sym}
                                </Typography>
                            </Stack>

                            {/* Action chip */}
                            <Box sx={{ display: "inline-flex" }}>
                                <Typography
                                    variant="tableHeader"
                                    sx={{ color: col, bgcolor: `${col}18`, px: 0.75, py: 0.25, borderRadius: "var(--radius-sm)", fontWeight: "var(--font-weight-bold)" }}
                                >
                                    {s.action}
                                </Typography>
                            </Box>

                            {/* Score */}
                            <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="flex-end">
                                <Box sx={{ width: 36, height: 4, borderRadius: "var(--radius-full)", bgcolor: "background.tertiary", overflow: "hidden" }}>
                                    <Box sx={{ width: `${s.score}%`, height: "100%", bgcolor: col, borderRadius: "var(--radius-full)", transition: "width 1s ease" }} />
                                </Box>
                                <Typography variant="tableHeader" sx={{ color: col, fontWeight: "var(--font-weight-bold)" }}>
                                    {s.score}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                })}

                {visible < SIGNALS.length && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main", animation: "pulse 1s ease infinite" }} />
                        <Typography variant="tableHeader">Scanning markets…</Typography>
                    </Stack>
                )}
            </Box>

            {/* Sentiment footer */}
            <Box
                sx={{
                    borderTop: "1px solid var(--color-border-primary)",
                    px: 2, py: 1.5,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    bgcolor: "background.secondary",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Activity size={14} color={theme.palette.success.main} />
                    <Typography variant="tableHeader" sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Market Sentiment
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 80, height: 4, borderRadius: "var(--radius-full)", bgcolor: "background.tertiary", overflow: "hidden" }}>
                        <Box
                            sx={{
                                width: `${sentiment}%`,
                                height: "100%",
                                background: "linear-gradient(to right, var(--color-success), var(--color-info))",
                                borderRadius: "var(--radius-full)",
                                transition: "width 1s ease",
                            }}
                        />
                    </Box>
                    <Typography variant="chipText" sx={{ color: "success.main", fontWeight: "var(--font-weight-bold)" }}>
                        {sentiment}% Bullish
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
};

/* ══════════════════════════════════════════════
   STAT CARD
══════════════════════════════════════════════ */
const StatCard = ({ icon, value, label }) => (
    <Box
        sx={{
            display: "flex", flexDirection: "column", gap: 0.75,
            p: 3,
            borderRadius: "var(--radius-lg)",
            bgcolor: "background.paper",
            border: "1px solid var(--color-border-primary)",
            boxShadow: "var(--shadow-sm)",
            backdropFilter: "blur(8px)",
            transition: "var(--transition-normal)",
            cursor: "default",
            "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "var(--shadow-lg)",
                borderColor: "primary.main",
            },
        }}
    >
        <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ color: "primary.main" }}>{icon}</Box>
            <Typography variant="title" sx={{ fontSize: "var(--font-size-2xl)" }}>
                {value}
            </Typography>
        </Stack>
        <Typography variant="tableHeader">{label}</Typography>
    </Box>
);

/* ══════════════════════════════════════════════
   MAIN LANDING PAGE
══════════════════════════════════════════════ */
const LandingPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { toggleTheme } = useAppTheme();

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    }, [isDark]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "background.default",
                color: "text.primary",
                position: "relative",
                overflow: "hidden",
                // ── FIX: make the page a flex column so the footer is always at the bottom ──
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* ── Background Layers ── */}
            <CandlestickChartBackground />

            {/* Dot grid */}
            <Box
                sx={{
                    position: "absolute", inset: 0,
                    backgroundImage:
                        "linear-gradient(rgba(59,130,246,.025) 1px,transparent 1px)," +
                        "linear-gradient(90deg,rgba(59,130,246,.025) 1px,transparent 1px)",
                    backgroundSize: "50px 50px",
                    pointerEvents: "none",
                }}
            />

            {/* Radial glow */}
            <Box
                sx={{
                    position: "absolute", inset: 0, pointerEvents: "none",
                    background: isDark
                        ? "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,150,255,.08) 0%, transparent 70%)"
                        : "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,150,255,.06) 0%, transparent 70%)",
                }}
            />

            {/* ══════════════════════════════════════════════
                NAVBAR
            ══════════════════════════════════════════════ */}
            <Box
                component="nav"
                sx={{
                    position: "relative", zIndex: 10,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: { xs: 3, md: 5 },
                    height: 64,
                    bgcolor: "background.default",
                    backdropFilter: "blur(16px)",
                    borderBottom: "1px solid var(--color-border-primary)",
                    "&::before": {
                        content: '""',
                        position: "absolute", inset: 0,
                        bgcolor: "background.default",
                        opacity: 0.8,
                        zIndex: -1,
                    },
                }}
            >
                {/* Logo */}
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                        sx={{
                            width: 32, height: 32,
                            borderRadius: "var(--radius-md)",
                            background: "linear-gradient(135deg, var(--color-primary), var(--color-info))",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <TrendingUp size={17} color="#fff" />
                    </Box>
                    <Typography
                        variant="subtitle"
                        sx={{ fontWeight: "var(--font-weight-bold)", color: "text.primary", letterSpacing: "-0.02em" }}
                    >
                        FactorAlpha
                    </Typography>
                </Stack>

                {/* Right actions */}
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton
                        onClick={toggleTheme}
                        size="small"
                        sx={{
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-md)",
                            color: "text.muted",
                            transition: "var(--transition-fast)",
                            "&:hover": {
                                borderColor: "primary.main",
                                color: "primary.main",
                                bgcolor: "primary.lighter",
                            },
                        }}
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </IconButton>

                    <Button
                        component={Link}
                        to="/login"
                        variant="outlined"
                        size="small"
                        sx={{
                            borderRadius: "var(--radius-md)",
                            borderColor: "var(--color-border-primary)",
                            color: "text.primary",
                            fontWeight: "var(--font-weight-semibold)",
                            transition: "var(--transition-fast)",
                            "&:hover": {
                                borderColor: "primary.main",
                                color: "primary.main",
                                bgcolor: "primary.lighter",
                            },
                        }}
                    >
                        Log In
                    </Button>
                </Stack>
            </Box>

            {/* ══════════════════════════════════════════════
                HERO
            ══════════════════════════════════════════════ */}
            <Box
                sx={{
                    position: "relative", zIndex: 5,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 5,
                    px: { xs: 3, md: 5 },
                    pt: { xs: 6, md: 8 },
                    pb: { xs: 8, md: 10 },
                    maxWidth: 1200, mx: "auto", width: "100%",
                }}
            >
                {/* ── Left copy ── */}
                <Box sx={{ flex: "1 1 400px", maxWidth: 560 }}>

                    {/* Live badge */}
                    <Box
                        sx={{
                            display: "inline-flex", alignItems: "center", gap: 1,
                            mb: 3.5,
                            bgcolor: "primary.lighter",
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: "var(--radius-full)",
                            px: 1.75, py: 0.75,
                            opacity: 0.9,
                            animation: "fadeIn 0.5s ease both",
                            "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 0.9 } },
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", animation: "pulse 1.5s ease infinite" }} />
                        <Typography
                            variant="tableHeader"
                            sx={{ color: "primary.main", letterSpacing: "0.08em", textTransform: "uppercase" }}
                        >
                            AI-Powered Engine
                        </Typography>
                    </Box>

                    {/* Headline */}
                    <Typography
                        variant="title"
                        component="h1"
                        sx={{
                            mb: 2.5,
                            lineHeight: 1.15,
                            letterSpacing: "-0.03em",
                            fontSize: { xs: "var(--font-size-4xl)", md: "var(--font-size-5xl)" },
                            animation: "fadeUp 0.6s ease both",
                            animationDelay: "0.1s",
                            "@keyframes fadeUp": {
                                from: { opacity: 0, transform: "translateY(20px)" },
                                to: { opacity: 1, transform: "translateY(0)" },
                            },
                        }}
                    >
                        Stop guessing.{" "}
                        <Box
                            component="span"
                            sx={{
                                background: "linear-gradient(135deg, var(--color-primary), var(--color-info))",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Start knowing.
                        </Box>
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                        variant="paragraph"
                        sx={{
                            mb: 4.5, maxWidth: 460, lineHeight: 1.75,
                            color: "text.secondary",
                            animation: "fadeUp 0.6s ease both",
                            animationDelay: "0.2s",
                        }}
                    >
                        Factor-based scoring meets real-time market intelligence.
                        Every signal is ranked, explained, and ready to act on.
                    </Typography>

                    {/* CTA */}
                    <Stack
                        direction="row" spacing={1.75} flexWrap="wrap"
                        sx={{ animation: "fadeUp 0.6s ease both", animationDelay: "0.3s", mt: 2 }}
                    >
                        <Button
                            component={Link}
                            to="/signup"
                            variant="contained"
                            endIcon={<ArrowRight size={16} />}
                            sx={{
                                background: "linear-gradient(to right, var(--color-primary), var(--color-info))",
                                borderRadius: "var(--radius-lg)",
                                fontWeight: "var(--font-weight-semibold)",
                                px: 3.5, py: 1.5,
                                boxShadow: "var(--shadow-md)",
                                transition: "var(--transition-normal)",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 20px 40px rgba(0,150,255,.35)",
                                },
                            }}
                        >
                            Get Started Free
                        </Button>
                    </Stack>
                </Box>

                {/* ── Right terminal ── */}
                <Box sx={{ flex: "1 1 380px", display: "flex", justifyContent: "center" }}>
                    <SignalTerminal />
                </Box>
            </Box>

            {/* ══════════════════════════════════════════════
                STATS ROW
            ══════════════════════════════════════════════ */}
            <Box sx={{ position: "relative", zIndex: 5, maxWidth: 1200, mx: "auto", width: "100%", px: { xs: 3, md: 5 }, pb: 10 }}>
                <Grid container spacing={2} justifyContent="center">
                    {[
                        { icon: <Zap size={20} />, value: "2+", label: "Markets" },
                        { icon: <BarChart2 size={20} />, value: "AI Analyzer", label: "Accurate Portfolio Analyzer" },
                        { icon: <ShieldCheck size={20} />, value: "12+", label: "Factor Models Running" },
                        { icon: <Activity size={20} />, value: "Real-time", label: "Market Data Updates" },
                    ].map(({ icon, value, label }) => (
                        <Grid item xs={12} sm={6} md={3} key={label}>
                            <StatCard icon={icon} value={value} label={label} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ══════════════════════════════════════════════
                FOOTER — mt: "auto" pins it to the bottom
            ══════════════════════════════════════════════ */}
            <Box
                sx={{
                    position: "relative", zIndex: 5,
                    mt: "auto",                                          // ← the fix
                    borderTop: "1px solid var(--color-border-primary)",
                    px: { xs: 3, md: 5 }, py: 2.5,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 1.5,
                }}
            >
                <Typography variant="tableHeader" sx={{ color: "text.muted" }}>
                    © 2026 FactorAlpha.
                </Typography>
                <Stack direction="row" spacing={3}>
                    {["Privacy", "Terms", "Contact"].map((l) => (
                        <Typography
                            key={l}
                            component="a"
                            href="#"
                            variant="tableHeader"
                            sx={{
                                textDecoration: "none",
                                color: "text.muted",
                                transition: "var(--transition-fast)",
                                "&:hover": { color: "primary.main" },
                            }}
                        >
                            {l}
                        </Typography>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

export default LandingPage;